import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import moment from "moment";
import { Feather } from "@expo/vector-icons";

import { useColumnResize } from "../../Reports/hooks/useColumnResize";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { useTranslation } from "../../../utils/useTranslation";
import { translations } from "../../../utils/translations";
import { exportDataToExcel } from "../../../utils/exportDataToExcel";
import { getWorkOrderPlanning } from "../../../services/workOrderPlanningService";
import {
  calendarAttendanceModel,
  workCenterCalendarModel,
  workCenterPlanningModel,
  workOrderPlanningModel,
} from "../../../models/reportsModel";

const DAY_W = 44; // px per day column
const BAR_H = 24;
const BAR_GAP = 4;
const ROW_PAD = 6;
const LEFT_W = 130; // frozen work-centre label column
const MONTH_H = 26;
const DAY_H = 24;
const TOTAL_H = 40;
const WINDOW_MONTHS = 3;
const POPOVER_W = 300;
const CARET_H = 7;

// Odoo mrp.workorder states. The Odoo planning view shows the open ones by default.
const OPEN_STATES = ["progress", "ready", "waiting", "pending"];
const ALL_STATES = [...OPEN_STATES, "done", "cancel"];

const STATE_LABELS: Record<string, keyof typeof translations> = {
  progress: "workOrderStateProgress",
  ready: "workOrderStateReady",
  waiting: "workOrderStateWaiting",
  pending: "workOrderStatePending",
  done: "workOrderStateDone",
  cancel: "workOrderStateCancel",
};

const BAR_COLORS = [
  "#E8A87C", "#C38D9E", "#85CDCB", "#E27D60", "#41B3A3", "#A2C5AC",
  "#F5B7B1", "#AEC6CF", "#D7BDE2", "#F9E79F", "#A9CCE3", "#B9E3C6",
];

const MS_PER_DAY = 86400000;

// Odoo's REST layer serialises datetimes as naive UTC, while the Odoo UI renders
// them in the plant's timezone. Convert so this grid agrees with what Odoo shows.
const SITE_TIME_ZONE = "Australia/Sydney";

const fieldsFormatCache = new Map<string, Intl.DateTimeFormat>();

const fieldsFormatFor = (timeZone: string) => {
  let format = fieldsFormatCache.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    fieldsFormatCache.set(timeZone, format);
  }
  return format;
};

// "YYYY-MM-DD HH:mm:ss" (UTC) -> a Date whose *local* fields carry the
// wall-clock time in `timeZone`, so calculations downstream stay timezone-free.
// Shift windows come from each work centre's own calendar timezone, which is
// not always the same as the timezone the grid is drawn in.
const parseOdooDateIn = (value: string | null, timeZone: string) => {
  if (!value) return null;
  const instant = new Date(`${value.replace(" ", "T")}Z`);
  if (Number.isNaN(instant.getTime())) return null;
  const parts = fieldsFormatFor(timeZone).formatToParts(instant);
  const field = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);
  return new Date(
    field("year"),
    field("month") - 1,
    field("day"),
    field("hour") % 24, // some engines emit hour 24 for midnight
    field("minute"),
    field("second"),
  );
};

const parseOdooDate = (value: string | null) =>
  parseOdooDateIn(value, SITE_TIME_ZONE);

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toISODate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const formatDisplayDate = (date: Date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;

// "20/08/2026, 2:07:41 pm"
const formatDateTime = (value: string | null) => {
  const date = parseOdooDate(value);
  if (!date) return "—";
  const time = date.toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${formatDisplayDate(date)}, ${time}`;
};

// "01/08/2026" -> Date, or null if not a valid DD/MM/YYYY string
const parseDMY = (text: string) => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!match) return null;
  const [, d, m, y] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  const valid =
    date.getFullYear() === Number(y) &&
    date.getMonth() === Number(m) - 1 &&
    date.getDate() === Number(d);
  return valid ? date : null;
};

interface rangePreset {
  key: string;
  labelKey: keyof typeof translations;
  range: () => { start: Date; end: Date };
}

const RANGE_PRESETS: Array<rangePreset> = [
  {
    key: "today",
    labelKey: "today",
    range: () => ({ start: moment().startOf("day").toDate(), end: moment().startOf("day").toDate() }),
  },
  {
    key: "week",
    labelKey: "thisWeek",
    range: () => ({ start: moment().startOf("isoWeek").toDate(), end: moment().endOf("isoWeek").toDate() }),
  },
  {
    key: "month",
    labelKey: "thisMonth",
    range: () => ({ start: moment().startOf("month").toDate(), end: moment().endOf("month").toDate() }),
  },
  {
    key: "quarter",
    labelKey: "thisQuarter",
    range: () => ({ start: moment().startOf("quarter").toDate(), end: moment().endOf("quarter").toDate() }),
  },
  {
    key: "year",
    labelKey: "thisYear",
    range: () => ({ start: moment().startOf("year").toDate(), end: moment().endOf("year").toDate() }),
  },
];

// Keep work centres in a stable running order: numbered lines ascending
// ("Line 1" … "Line 4"), then any named centre such as Relabelling.
const compareWorkCentres = (a: string, b: string) => {
  const left = /^(.*?)\s*(\d+)\s*$/.exec(a);
  const right = /^(.*?)\s*(\d+)\s*$/.exec(b);
  if (left && right) {
    const byPrefix = left[1].localeCompare(right[1]);
    return byPrefix !== 0 ? byPrefix : Number(left[2]) - Number(right[2]);
  }
  if (left) return -1;
  if (right) return 1;
  return a.localeCompare(b);
};

const dayDiff = (from: Date, to: Date) =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY);

// Minutes -> "81:30"
const formatMinutes = (minutes: number) => {
  const rounded = Math.round(minutes);
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, "0")}`;
};

// Odoo's duration columns read as minutes:seconds, e.g. 47.03 min -> "47:02".
const formatDurationMinutes = (minutes: number) => {
  const whole = Math.floor(minutes);
  const seconds = Math.round((minutes - whole) * 60);
  return `${whole}:${String(seconds).padStart(2, "0")}`;
};

// List-view columns, mirroring Odoo's list of work orders.
const LIST_COLUMNS = [
  { key: "operation",  labelKey: "operation"         as const, width: 90,  align: "left"  as const },
  { key: "workcenter", labelKey: "workCenter"        as const, width: 90,  align: "left"  as const },
  { key: "mo",         labelKey: "manufacturingOrder" as const, width: 150, align: "left"  as const },
  { key: "customerReference", labelKey: "customerReference" as const, width: 160, align: "left" as const },
  { key: "product",    labelKey: "product"           as const, width: 330, align: "left"  as const },
  { key: "qty",        labelKey: "quantityRequired"  as const, width: 110, align: "right" as const },
  { key: "expected",   labelKey: "expectedDuration"  as const, width: 120, align: "right" as const },
  { key: "duration",   labelKey: "durationMinutes"   as const, width: 130, align: "right" as const },
  { key: "status",     labelKey: "status"            as const, width: 140, align: "left"  as const },
];

const DEFAULT_LIST_WIDTHS: Record<string, number> = LIST_COLUMNS.reduce(
  (acc, column) => ({ ...acc, [column.key]: column.width }),
  {},
);

// Drag strip on the right edge of each header cell (web only, as elsewhere).
const RESIZE_HANDLE = {
  position: "absolute" as const,
  right: 0,
  top: 0,
  bottom: 0,
  width: 6,
  cursor: "col-resize",
  zIndex: 2,
};

const colorFor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  return BAR_COLORS[Math.abs(hash) % BAR_COLORS.length];
};

interface positionedWorkOrder {
  order: workOrderPlanningModel;
  startIndex: number;
  endIndex: number;
  lane: number;
}

interface plannedRow {
  key: string;
  name: string;
  calendar: workCenterCalendarModel | null;
  bars: Array<positionedWorkOrder>;
  laneCount: number;
  height: number;
  top: number;
}

interface popoverAnchor {
  order: workOrderPlanningModel;
  barTop: number;
  barBottom: number;
  left: number;
}

// Fallback until the popover has been measured once.
const POPOVER_EST_H = 150;

// An order matches if ANY search criteria matches, so "cj-190r1, cj-212lb"
// shows both item families.
const matchesSearch = (order: workOrderPlanningModel, terms: Array<string>) => {
  if (terms.length === 0) return true;
  const haystack = [
    order.product_name,
    order.mo_reference,
    order.name,
    order.workcenter_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return terms.some((term) => haystack.includes(term));
};

// Minutes a work order occupies on each day, counting only time that falls
// inside its work centre's shift windows — this is what Odoo's planning view
// totals, so non-working days (weekends here) contribute nothing.
const addWorkingMinutes = (
  totals: Array<number>,
  order: workOrderPlanningModel,
  calendar: workCenterCalendarModel | null,
  rangeStart: Date,
  totalDays: number,
) => {
  const timeZone = calendar?.tz || SITE_TIME_ZONE;
  const start = parseOdooDateIn(order.date_start, timeZone);
  const finish = parseOdooDateIn(order.date_finished, timeZone) ?? start;
  if (!start || !finish || finish <= start) return;

  const windowsByDay = new Map<number, Array<calendarAttendanceModel>>();
  (calendar?.attendances ?? []).forEach((attendance) => {
    const day = Number(attendance.dayofweek);
    if (!windowsByDay.has(day)) windowsByDay.set(day, []);
    windowsByDay.get(day)!.push(attendance);
  });

  const lastDay = startOfDay(finish);
  for (
    const cursor = startOfDay(start);
    cursor.getTime() <= lastDay.getTime();
    cursor.setDate(cursor.getDate() + 1)
  ) {
    const index = dayDiff(rangeStart, cursor);
    if (index < 0 || index >= totalDays) continue;

    // Odoo's dayofweek: "0" = Monday, JS getDay(): 0 = Sunday.
    const windows = windowsByDay.get((cursor.getDay() + 6) % 7);
    if (!windows) continue;

    windows.forEach((window) => {
      const windowStart = new Date(cursor);
      windowStart.setHours(0, 0, 0, 0);
      windowStart.setMinutes(Math.round(window.hour_from * 60));
      const windowEnd = new Date(cursor);
      windowEnd.setHours(0, 0, 0, 0);
      windowEnd.setMinutes(Math.round(window.hour_to * 60));

      const overlap =
        Math.min(finish.getTime(), windowEnd.getTime()) -
        Math.max(start.getTime(), windowStart.getTime());
      if (overlap > 0) totals[index] += overlap / 60000;
    });
  }
};

// Pack overlapping work orders into stacked lanes so no two bars collide.
const assignLanes = (orders: Array<positionedWorkOrder>) => {
  const laneEnds: Array<number> = [];
  orders
    .slice()
    .sort((a, b) => a.startIndex - b.startIndex)
    .forEach((item) => {
      let lane = laneEnds.findIndex((end) => end <= item.startIndex);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = item.endIndex + 1;
      item.lane = lane;
    });
  return laneEnds.length;
};

export default function WorkOrderPlanningScreen() {
  const { colors, isDark } = useAppTheme();
  const { t } = useTranslation();
  const { height: windowHeight } = useWindowDimensions();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);

  const [rangeStart, setRangeStart] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [rangeEnd, setRangeEnd] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + WINDOW_MONTHS, 0);
  });
  const [isRangePickerOpen, setIsRangePickerOpen] = useState<boolean>(false);
  const [fromText, setFromText] = useState<string>("");
  const [toText, setToText] = useState<string>("");
  const [groups, setGroups] = useState<Array<workCenterPlanningModel>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedStates, setSelectedStates] = useState<Array<string>>(OPEN_STATES);
  const [selected, setSelected] = useState<popoverAnchor | null>(null);
  const [popoverHeight, setPopoverHeight] = useState<number>(POPOVER_EST_H);
  const [searchCriteria, setSearchCriteria] = useState<Array<string>>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const searchScrollRef = useRef<any>(null);

  // Typing a comma commits the term as a criteria chip, same as the scorecard
  // screens' search box.
  const onSearchChange = (text: string) => {
    if (text.includes(",")) {
      const before = text.slice(0, text.indexOf(",")).trim();
      if (before) setSearchCriteria((current) => [...current, before]);
      setSearchInput("");
    } else {
      setSearchInput(text);
    }
  };

  const commitSearchInput = () => {
    const trimmed = searchInput.trim();
    if (trimmed) {
      setSearchCriteria((current) => [...current, trimmed]);
      setSearchInput("");
    }
  };
  const [viewMode, setViewMode] = useState<"gantt" | "list">("gantt");
  const [expandedKeys, setExpandedKeys] = useState<Array<string>>([]);
  const [listWidths, setListWidths] = useState<Record<string, number>>(DEFAULT_LIST_WIDTHS);
  const startResize = useColumnResize(setListWidths);

  const columnWidth = (key: string) => listWidths[key] ?? DEFAULT_LIST_WIDTHS[key];
  const listWidth = LIST_COLUMNS.reduce((sum, column) => sum + columnWidth(column.key), 0);

  // Committed chips plus whatever is still being typed, so results update live.
  const searchTerms = useMemo(
    () =>
      [...searchCriteria, searchInput.trim()]
        .map((term) => term.toLowerCase())
        .filter(Boolean),
    [searchCriteria, searchInput],
  );

  const totalDays = useMemo(() => dayDiff(rangeStart, rangeEnd) + 1, [rangeStart, rangeEnd]);
  const gridWidth = totalDays * DAY_W;

  const scrollAnim = useRef(new Animated.Value(0)).current;
  const negScrollAnim = useRef(Animated.multiply(scrollAnim, -1)).current;
  const onBodyScroll = useRef(
    Animated.event([{ nativeEvent: { contentOffset: { x: scrollAnim } } }], {
      useNativeDriver: false,
    }),
  ).current;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setSelected(null);
    // The gantt is scoped to its visible window; the list shows every work
    // order, matching how Odoo's two views behave.
    const request =
      viewMode === "list"
        ? getWorkOrderPlanning()
        : getWorkOrderPlanning(toISODate(rangeStart), toISODate(rangeEnd));
    request.then((data) => {
      if (cancelled) return;
      setGroups(data);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [rangeStart, rangeEnd, viewMode]);

  // Days that make up the timeline, plus the month spans drawn above them.
  const days = useMemo(
    () =>
      Array.from({ length: totalDays }, (_, index) => {
        const date = new Date(
          rangeStart.getFullYear(),
          rangeStart.getMonth(),
          rangeStart.getDate() + index,
        );
        const weekday = date.getDay();
        return { date, isWeekend: weekday === 0 || weekday === 6 };
      }),
    [rangeStart, totalDays],
  );

  const months = useMemo(() => {
    const result: Array<{ label: string; span: number }> = [];
    days.forEach(({ date }) => {
      const label = date.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
      const last = result[result.length - 1];
      if (last && last.label === label) last.span += 1;
      else result.push({ label, span: 1 });
    });
    return result;
  }, [days]);

  const todayIndex = useMemo(() => {
    const index = dayDiff(rangeStart, new Date());
    return index >= 0 && index < totalDays ? index : -1;
  }, [rangeStart, totalDays]);

  const rows: Array<plannedRow> = useMemo(() => {
    let offset = 0;
    return groups
      .slice()
      .sort((a, b) =>
        compareWorkCentres(a.workcenter_name ?? "", b.workcenter_name ?? ""),
      )
      .map((group) => {
        const bars: Array<positionedWorkOrder> = [];

        group.work_orders.forEach((order) => {
          if (!selectedStates.includes(order.state)) return;
          if (!matchesSearch(order, searchTerms)) return;
          const start = parseOdooDate(order.date_start);
          const finish = parseOdooDate(order.date_finished) ?? start;
          if (!start || !finish) return;

          // Clamp to the visible window so partially-visible bars still render.
          const rawStart = dayDiff(rangeStart, start);
          const rawEnd = dayDiff(rangeStart, finish);
          const startIndex = Math.max(0, Math.min(rawStart, totalDays - 1));
          const endIndex = Math.max(startIndex, Math.min(rawEnd, totalDays - 1));

          bars.push({ order, startIndex, endIndex, lane: 0 });
        });

        const laneCount = assignLanes(bars);
        const height = Math.max(1, laneCount) * (BAR_H + BAR_GAP) + ROW_PAD * 2;
        const top = offset;
        offset += height;

        return {
          key: String(group.workcenter_id ?? "unassigned"),
          name: group.workcenter_name ?? t("unassigned"),
          calendar: group.calendar ?? null,
          bars,
          laneCount,
          height,
          top,
        };
      });
  }, [groups, selectedStates, searchTerms, rangeStart, totalDays, t]);

  // Scheduled time per day, counted only within each work centre's shift
  // windows — matches how Odoo's planning view totals the row.
  const dailyMinutes = useMemo(() => {
    const totals = new Array(totalDays).fill(0);
    rows.forEach((row) =>
      row.bars.forEach(({ order }) =>
        addWorkingMinutes(totals, order, row.calendar, rangeStart, totalDays),
      ),
    );
    return totals;
  }, [rows, rangeStart, totalDays]);

  // List view: work centres in the same running order as the gantt, each with
  // its work orders and the column totals Odoo shows on the group row.
  const listGroups = useMemo(
    () =>
      groups
        .slice()
        .sort((a, b) =>
          compareWorkCentres(a.workcenter_name ?? "", b.workcenter_name ?? ""),
        )
        .map((group) => {
          const orders = group.work_orders.filter(
            (order) =>
              selectedStates.includes(order.state) &&
              matchesSearch(order, searchTerms),
          );
          return {
            key: String(group.workcenter_id ?? "unassigned"),
            name: group.workcenter_name ?? t("unassigned"),
            orders,
            expectedMinutes: orders.reduce((sum, o) => sum + (o.duration_expected ?? 0), 0),
            durationMinutes: orders.reduce((sum, o) => sum + (o.duration ?? 0), 0),
          };
        })
        .filter((group) => group.orders.length > 0),
    [groups, selectedStates, searchTerms, t],
  );

  const listTotals = useMemo(
    () => ({
      expectedMinutes: listGroups.reduce((sum, g) => sum + g.expectedMinutes, 0),
      durationMinutes: listGroups.reduce((sum, g) => sum + g.durationMinutes, 0),
      count: listGroups.reduce((sum, g) => sum + g.orders.length, 0),
    }),
    [listGroups],
  );

  const toggleExpanded = (key: string) =>
    setExpandedKeys((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );

  const visibleCount = useMemo(
    () => rows.reduce((sum, row) => sum + row.bars.length, 0),
    [rows],
  );

  // Exports exactly what's currently on screen — same work centre order,
  // same status/search filters, and (for the gantt) the same date window —
  // as a flat text/number table rather than a picture of the chart.
  const exportToExcel = () => {
    const source: Array<workOrderPlanningModel> =
      viewMode === "list"
        ? listGroups.flatMap((group) => group.orders)
        : rows.flatMap((row) => row.bars.map((bar) => bar.order));

    const data = source.map((order) => ({
      [t("workCenter")]: order.workcenter_name ?? "",
      [t("manufacturingOrder")]: order.mo_reference ?? "",
      [t("customerReference")]: order.customer_reference ?? t("unknown"),
      [t("product")]: order.product_name ?? "",
      [t("operation")]: order.name,
      [t("startDate")]: formatDateTime(order.date_start),
      [t("stopDate")]: formatDateTime(order.date_finished),
      [t("expectedDuration")]: formatDurationMinutes(order.duration_expected ?? 0),
      [t("durationMinutes")]: formatDurationMinutes(order.duration ?? 0),
      [t("quantityRequired")]: order.qty_production ?? 0,
      [t("status")]: t(STATE_LABELS[order.state]),
    }));

    const windowTag =
      viewMode === "gantt" ? `_${toISODate(rangeStart)}_to_${toISODate(rangeEnd)}` : "";
    exportDataToExcel(
      data,
      `Work_Orders_Planning${windowTag}_${new Date().toISOString().slice(0, 10)}`,
      "Work Orders Planning",
    );
  };

  const contentHeight = useMemo(
    () => rows.reduce((sum, row) => sum + row.height, 0),
    [rows],
  );

  const toggleState = (state: string) =>
    setSelectedStates((current) =>
      current.includes(state)
        ? current.filter((item) => item !== state)
        : [...current, state],
    );

  const openRangePicker = () => {
    setFromText(formatDisplayDate(rangeStart));
    setToText(formatDisplayDate(rangeEnd));
    setIsRangePickerOpen(true);
  };

  const applyPreset = (preset: rangePreset) => {
    const { start, end } = preset.range();
    setRangeStart(start);
    setRangeEnd(end);
    setIsRangePickerOpen(false);
  };

  // Shifts the whole window by 3 calendar months, keeping its current span —
  // independent of whichever preset produced the current range.
  const shiftWindow = (direction: 1 | -1) => {
    setRangeStart((current) => moment(current).add(3 * direction, "months").toDate());
    setRangeEnd((current) => moment(current).add(3 * direction, "months").toDate());
  };

  const applyCustomRange = () => {
    const from = parseDMY(fromText);
    const to = parseDMY(toText);
    if (!from || !to) return;
    setRangeStart(from <= to ? from : to);
    setRangeEnd(from <= to ? to : from);
    setIsRangePickerOpen(false);
  };

  // Shrink to the actual row content when it's shorter than the available
  // viewport, so the Total footer sits directly under the last row instead
  // of leaving a gap; only scroll (and cap height) once content overflows.
  const bodyHeight = Math.min(Math.max(200, windowHeight - 340), contentHeight);

  return (
    <View style={styles.container}>
      <View style={styles.toolbar}>
        <View style={{ flexDirection: "row", alignItems: "baseline", gap: 8 }}>
          <Text style={styles.title}>QLD Work Order Planning</Text>
          <Text style={{ fontSize: 10, fontStyle: "italic", color: isDark ? colors.textSecondary : "#64748B" }}>
            Integrated from Odoo
          </Text>
        </View>

        {/* The date window only applies to the gantt, so hide it in list view
            (Odoo does the same — its list is not date-scoped). */}
        <View style={[styles.rangeGroup, viewMode === "list" && styles.hidden]}>
          <Pressable style={styles.navButton} onPress={() => shiftWindow(-1)}>
            <Text style={styles.navButtonText}>‹</Text>
          </Pressable>

          <View style={styles.rangeFieldWrapper}>
            <Pressable style={styles.rangeField} onPress={openRangePicker}>
              <Text style={styles.rangeFieldText}>
                📅 {formatDisplayDate(rangeStart)} → {formatDisplayDate(rangeEnd)}
              </Text>
            </Pressable>

            {isRangePickerOpen && (
              <>
                <Pressable
                  style={styles.rangeBackdrop}
                  onPress={() => setIsRangePickerOpen(false)}
                />
                <View style={styles.rangeDropdown}>
                {RANGE_PRESETS.map((preset) => (
                  <Pressable
                    key={preset.key}
                    style={styles.presetRow}
                    onPress={() => applyPreset(preset)}
                  >
                    <Text style={styles.presetText}>{t(preset.labelKey)}</Text>
                  </Pressable>
                ))}

                <View style={styles.rangeDivider} />

                <View style={styles.customRow}>
                  <Text style={styles.customCheck}>✓</Text>
                  <Text style={styles.customLabel}>{t("from")}</Text>
                  <TextInput
                    style={styles.customInput}
                    value={fromText}
                    onChangeText={setFromText}
                    placeholder="DD/MM/YYYY"
                    placeholderTextColor={isDark ? "#777" : "#bbb"}
                  />
                  <Text style={styles.customLabel}>{t("to")}</Text>
                  <TextInput
                    style={styles.customInput}
                    value={toText}
                    onChangeText={setToText}
                    placeholder="DD/MM/YYYY"
                    placeholderTextColor={isDark ? "#777" : "#bbb"}
                  />
                  <Pressable style={styles.applyButton} onPress={applyCustomRange}>
                    <Text style={styles.applyButtonText}>{t("apply")}</Text>
                  </Pressable>
                </View>
              </View>
            </>
          )}
          </View>

          <Pressable style={styles.navButton} onPress={() => shiftWindow(1)}>
            <Text style={styles.navButtonText}>›</Text>
          </Pressable>
        </View>

        <View style={styles.searchContainer}>
          <ScrollView
            ref={searchScrollRef}
            onContentSizeChange={() =>
              searchScrollRef.current?.scrollToEnd({ animated: true })
            }
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ flex: 1 }}
            contentContainerStyle={{ alignItems: "center", flexGrow: 1 }}
          >
            {searchCriteria.map((term, index) => (
              <View key={`${term}-${index}`} style={styles.searchChip}>
                <Text style={styles.searchChipText}>{term}</Text>
                <Pressable
                  style={styles.searchChipRemove}
                  onPress={() =>
                    setSearchCriteria((current) => current.filter((_, i) => i !== index))
                  }
                >
                  <Text style={styles.searchChipX}>✕</Text>
                </Pressable>
              </View>
            ))}
            <TextInput
              style={styles.searchInputInner}
              value={searchInput}
              onChangeText={onSearchChange}
              placeholder={searchCriteria.length === 0 ? t("searchBar") : t("addCriteria")}
              placeholderTextColor="#aaa"
              onSubmitEditing={commitSearchInput}
              blurOnSubmit={false}
            />
          </ScrollView>
        </View>

        <View style={styles.chipGroup}>
          {ALL_STATES.map((state) => {
            const active = selectedStates.includes(state);
            return (
              <Pressable
                key={state}
                onPress={() => toggleState(state)}
                style={[styles.chip, active && styles.chipActive]}
              >
                <Text style={[styles.chipText, active && styles.chipTextActive]}>
                  {t(STATE_LABELS[state])}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <Text style={styles.countText}>
          {viewMode === "list" ? listTotals.count : visibleCount} {t("workOrders")}
        </Text>

        <View style={styles.viewToggle}>
          {(["gantt", "list"] as const).map((mode) => (
            <Pressable
              key={mode}
              onPress={() => setViewMode(mode)}
              style={[styles.viewButton, viewMode === mode && styles.viewButtonActive]}
            >
              <Text
                style={[styles.viewButtonText, viewMode === mode && styles.viewButtonTextActive]}
              >
                {t(mode === "gantt" ? "ganttView" : "listView")}
              </Text>
            </Pressable>
          ))}
        </View>

        <Pressable style={styles.downloadButton} onPress={exportToExcel}>
          <Feather name="download" size={14} color={isDark ? "#aaa" : "#6B7280"} />
        </Pressable>
      </View>

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#1976d2" />
          <Text style={styles.loadingText}>{t("loading")}</Text>
        </View>
      ) : viewMode === "list" ? (
        listGroups.length === 0 ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>{t("noData")}</Text>
          </View>
        ) : (
          <ScrollView horizontal showsHorizontalScrollIndicator>
            <View style={{ width: listWidth }}>
              {/* header — each cell carries a drag strip on its right edge */}
              <View style={styles.listHeaderRow}>
                {LIST_COLUMNS.map((column) => (
                  <View key={column.key} style={{ width: columnWidth(column.key) }}>
                    <Text
                      style={[styles.listHeaderText, { textAlign: column.align }]}
                      numberOfLines={1}
                    >
                      {t(column.labelKey)}
                    </Text>
                    {Platform.OS === "web" && (
                      <View
                        style={RESIZE_HANDLE}
                        {...({
                          onMouseDown: (event: any) =>
                            startResize(event, column.key, columnWidth(column.key)),
                        } as any)}
                      />
                    )}
                  </View>
                ))}
              </View>

              <ScrollView style={{ maxHeight: Math.max(200, windowHeight - 300) }} nestedScrollEnabled>
                {listGroups.map((group) => {
                  const isExpanded = expandedKeys.includes(group.key);
                  return (
                    <View key={group.key}>
                      <Pressable
                        style={styles.listGroupRow}
                        onPress={() => toggleExpanded(group.key)}
                      >
                        <Text style={styles.listGroupLabel}>
                          {isExpanded ? "▼" : "▶"}  {group.name} ({group.orders.length})
                        </Text>
                        <Text style={[styles.listGroupCell, { width: columnWidth("expected") }]}>
                          {formatDurationMinutes(group.expectedMinutes)}
                        </Text>
                        <Text style={[styles.listGroupCell, { width: columnWidth("duration") }]}>
                          {formatDurationMinutes(group.durationMinutes)}
                        </Text>
                        <View style={{ width: columnWidth("status") }} />
                      </Pressable>

                      {isExpanded &&
                        group.orders.map((order, index) => (
                          <View
                            key={order.id}
                            style={[styles.listRow, index % 2 === 1 && styles.listRowAlt]}
                          >
                            <Text style={[styles.listCell, { width: columnWidth("operation") }]} numberOfLines={1}>
                              {order.name}
                            </Text>
                            <Text style={[styles.listCell, { width: columnWidth("workcenter") }]} numberOfLines={1}>
                              {order.workcenter_name}
                            </Text>
                            <Text style={[styles.listCell, { width: columnWidth("mo") }]} numberOfLines={1}>
                              {order.mo_reference}
                            </Text>
                            <Text
                              style={[styles.listCell, { width: columnWidth("customerReference") }]}
                              numberOfLines={1}
                            >
                              {order.customer_reference ?? t("unknown")}
                            </Text>
                            <Text style={[styles.listCell, { width: columnWidth("product") }]} numberOfLines={1}>
                              {order.product_name}
                            </Text>
                            <Text style={[styles.listCell, styles.listCellRight, { width: columnWidth("qty") }]}>
                              {(order.qty_production ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Text>
                            <Text style={[styles.listCell, styles.listCellRight, { width: columnWidth("expected") }]}>
                              {formatDurationMinutes(order.duration_expected ?? 0)}
                            </Text>
                            <Text style={[styles.listCell, styles.listCellRight, { width: columnWidth("duration") }]}>
                              {formatDurationMinutes(order.duration ?? 0)}
                            </Text>
                            <View style={{ width: columnWidth("status"), justifyContent: "center" }}>
                              <View style={styles.statusChip}>
                                <Text style={styles.statusChipText} numberOfLines={1}>
                                  {t(STATE_LABELS[order.state])}
                                </Text>
                              </View>
                            </View>
                          </View>
                        ))}
                    </View>
                  );
                })}
              </ScrollView>

              {/* grand totals */}
              <View style={styles.listTotalRow}>
                <View
                  style={{
                    width: LIST_COLUMNS.slice(0, 6).reduce(
                      (sum, column) => sum + columnWidth(column.key),
                      0,
                    ),
                  }}
                />
                <Text style={[styles.listTotalCell, { width: columnWidth("expected") }]}>
                  {formatDurationMinutes(listTotals.expectedMinutes)}
                </Text>
                <Text style={[styles.listTotalCell, { width: columnWidth("duration") }]}>
                  {formatDurationMinutes(listTotals.durationMinutes)}
                </Text>
                <View style={{ width: columnWidth("status") }} />
              </View>
            </View>
          </ScrollView>
        )
      ) : rows.length === 0 ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t("noData")}</Text>
        </View>
      ) : (
        <>
          {/* Frozen month + day header, panned by the body's horizontal scroll */}
          <View style={styles.headerRow}>
            <View style={[styles.cornerCell, { width: LEFT_W }]}>
              <Text style={styles.cornerText}>{t("operations")}</Text>
            </View>
            <View style={styles.headerClip}>
              <Animated.View
                style={{ width: gridWidth, transform: [{ translateX: negScrollAnim }] }}
              >
                <View style={styles.monthRow}>
                  {months.map((month, index) => (
                    <View
                      key={`${month.label}-${index}`}
                      style={[styles.monthCell, { width: month.span * DAY_W }]}
                    >
                      <Text style={styles.monthText}>{month.label}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.dayRow}>
                  {days.map(({ date, isWeekend }, index) => (
                    <View
                      key={index}
                      style={[
                        styles.dayCell,
                        isWeekend && styles.weekendCell,
                        index === todayIndex && styles.todayCell,
                      ]}
                    >
                      <Text style={styles.dayText}>
                        {String(date.getDate()).padStart(2, "0")}
                      </Text>
                    </View>
                  ))}
                </View>
              </Animated.View>
            </View>
          </View>

          {/* Body: work-centre labels stay put horizontally, bars scroll.
              flexGrow/flexShrink 0 override ScrollView's own flexGrow:1 base
              style, which would otherwise stretch it to fill the container and
              push the totals footer down to the bottom of the screen. */}
          <ScrollView
            style={{ height: bodyHeight, flexGrow: 0, flexShrink: 0 }}
            nestedScrollEnabled
          >
            <View style={{ flexDirection: "row" }}>
              <View style={{ width: LEFT_W }}>
                {rows.map((row) => (
                  <View key={row.key} style={[styles.labelCell, { height: row.height }]}>
                    <Text style={styles.labelText}>{row.name}</Text>
                  </View>
                ))}
              </View>

              <ScrollView
                horizontal
                onScroll={onBodyScroll}
                scrollEventThrottle={16}
                showsHorizontalScrollIndicator
              >
                <View style={{ width: gridWidth }}>
                  {rows.map((row) => (
                    <View key={row.key} style={[styles.gridRow, { height: row.height }]}>
                      {/* day grid lines / weekend + today shading */}
                      {days.map(({ isWeekend }, index) => (
                        <View
                          key={index}
                          style={[
                            styles.gridCell,
                            { left: index * DAY_W },
                            isWeekend && styles.weekendCell,
                            index === todayIndex && styles.todayCell,
                          ]}
                        />
                      ))}

                      {row.bars.map(({ order, startIndex, endIndex, lane }) => {
                        const isClosed = order.state === "done" || order.state === "cancel";
                        const barTop = ROW_PAD + lane * (BAR_H + BAR_GAP);
                        return (
                          <Pressable
                            key={order.id}
                            onPress={() =>
                              setSelected({
                                order,
                                barTop: row.top + barTop,
                                barBottom: row.top + barTop + BAR_H,
                                left: startIndex * DAY_W,
                              })
                            }
                            style={[
                              styles.bar,
                              {
                                left: startIndex * DAY_W + 1,
                                width: (endIndex - startIndex + 1) * DAY_W - 2,
                                top: barTop,
                                backgroundColor: colorFor(
                                  order.product_name ?? String(order.id),
                                ),
                                opacity: isClosed ? 0.45 : 1,
                                borderColor:
                                  selected?.order.id === order.id
                                    ? "#1976d2"
                                    : "transparent",
                              },
                            ]}
                          >
                            <Text style={styles.barText} numberOfLines={1}>
                              {order.mo_reference} · {order.product_name}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  ))}

                  {/* Anchored info popover — rendered after the rows so it
                      paints above them, and inside the grid so it tracks scroll. */}
                  {selected && (() => {
                    // Flip above the bar when there isn't room below, so the
                    // card never gets clipped on the bottom rows.
                    const flipAbove =
                      selected.barBottom + CARET_H + popoverHeight > contentHeight;
                    const popoverTop = flipAbove
                      ? Math.max(0, selected.barTop - CARET_H - popoverHeight)
                      : selected.barBottom + CARET_H;
                    const popoverLeft = Math.max(
                      0,
                      Math.min(selected.left, gridWidth - POPOVER_W),
                    );
                    const caretLeft = Math.max(
                      10,
                      Math.min(
                        selected.left - popoverLeft + DAY_W / 2,
                        POPOVER_W - 24,
                      ),
                    );
                    return (
                    <View
                      onLayout={(event) => setPopoverHeight(event.nativeEvent.layout.height)}
                      style={[
                        styles.popover,
                        { top: popoverTop, left: popoverLeft },
                      ]}
                    >
                      <View
                        style={[
                          flipAbove ? styles.popoverCaretBottom : styles.popoverCaret,
                          { left: caretLeft },
                        ]}
                      />
                      <View style={styles.popoverHeader}>
                        <Text style={styles.popoverTitle}>
                          {[
                            selected.order.product_name,
                            selected.order.mo_reference,
                            selected.order.name,
                          ]
                            .filter(Boolean)
                            .join(" - ")}
                        </Text>
                        <Pressable
                          style={styles.popoverClose}
                          onPress={() => setSelected(null)}
                        >
                          <Text style={styles.popoverCloseText}>✕</Text>
                        </Pressable>
                      </View>
                      <View style={styles.popoverBody}>
                        <Text style={styles.popoverRow}>
                          <Text style={styles.popoverLabel}>{t("startDate")}: </Text>
                          {formatDateTime(selected.order.date_start)}
                        </Text>
                        <Text style={styles.popoverRow}>
                          <Text style={styles.popoverLabel}>{t("stopDate")}: </Text>
                          {formatDateTime(selected.order.date_finished)}
                        </Text>
                        <Text style={styles.popoverRow}>
                          <Text style={styles.popoverLabel}>{t("workcenter")}: </Text>
                          {selected.order.workcenter_name ?? t("unassigned")}
                        </Text>
                        {/* Traced through the MO's source document to the
                            originating sales order. Not every MO has one, so
                            this always renders rather than hiding when empty. */}
                        <Text style={styles.popoverRow}>
                          <Text style={styles.popoverLabel}>
                            {t("customerReference")}:{" "}
                          </Text>
                          {selected.order.customer_reference ?? t("unknown")}
                        </Text>
                      </View>
                    </View>
                    );
                  })()}
                </View>
              </ScrollView>
            </View>
          </ScrollView>

          {/* Frozen totals footer */}
          <View style={styles.footerRow}>
            <View style={[styles.footerLabel, { width: LEFT_W }]}>
              <Text style={styles.footerLabelText}>{t("total")}</Text>
            </View>
            <View style={styles.headerClip}>
              <Animated.View
                style={{
                  width: gridWidth,
                  flexDirection: "row",
                  transform: [{ translateX: negScrollAnim }],
                }}
              >
                {dailyMinutes.map((minutes, index) => (
                  <View
                    key={index}
                    style={[
                      styles.totalCell,
                      days[index]?.isWeekend && styles.weekendCell,
                      index === todayIndex && styles.todayCell,
                    ]}
                  >
                    {minutes > 0 && (
                      <Text style={styles.totalText}>{formatMinutes(minutes)}</Text>
                    )}
                  </View>
                ))}
              </Animated.View>
            </View>
          </View>
        </>
      )}
    </View>
  );
}

const createStyles = (colors: any, isDark: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: isDark ? colors.background : "#f5f5f5",
      padding: 12,
    },
    toolbar: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 12,
      marginBottom: 10,
      // Own stacking context so the range dropdown (position:absolute inside
      // it) paints above the Gantt grid below, which renders later in the DOM.
      position: "relative",
      zIndex: 50,
    },
    title: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
    },
    rangeGroup: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    navButton: {
      width: 28,
      alignItems: "center",
      paddingVertical: 6,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    navButtonText: {
      fontSize: 16,
      lineHeight: 20,
      color: colors.text,
    },
    rangeFieldWrapper: {
      position: "relative",
    },
    rangeField: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    rangeFieldText: {
      fontSize: 12,
      color: colors.text,
    },
    rangeBackdrop: {
      position: "absolute",
      top: -1000,
      left: -1000,
      right: -1000,
      bottom: -1000,
      zIndex: 29,
    },
    rangeDropdown: {
      position: "absolute",
      top: "100%",
      left: 0,
      marginTop: 4,
      width: 300,
      zIndex: 30,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      paddingVertical: 6,
      shadowColor: "#000",
      shadowOpacity: 0.2,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 4 },
      elevation: 8,
    },
    presetRow: {
      paddingHorizontal: 14,
      paddingVertical: 8,
    },
    presetText: {
      fontSize: 13,
      color: colors.text,
    },
    rangeDivider: {
      height: 1,
      backgroundColor: colors.borderSubtle,
      marginVertical: 6,
    },
    customRow: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 6,
      paddingHorizontal: 14,
      paddingVertical: 4,
    },
    customCheck: {
      fontSize: 13,
      color: "#1976d2",
      fontWeight: "700",
    },
    customLabel: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    customInput: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 4,
      paddingHorizontal: 6,
      paddingVertical: 3,
      fontSize: 12,
      color: colors.text,
      backgroundColor: isDark ? "#1e1e1e" : "#fff",
      minWidth: 88,
    },
    applyButton: {
      marginLeft: "auto",
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 4,
      backgroundColor: "#1976d2",
    },
    applyButtonText: {
      fontSize: 12,
      fontWeight: "700",
      color: "#fff",
    },
    chipGroup: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
    },
    chip: {
      paddingHorizontal: 10,
      paddingVertical: 3,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    chipActive: {
      borderColor: "#1976d2",
      backgroundColor: isDark ? "#123" : "#e3f2fd",
    },
    chipText: {
      fontSize: 11,
      color: colors.textMuted,
      textTransform: "capitalize",
    },
    chipTextActive: {
      color: "#1976d2",
      fontWeight: "700",
    },
    countText: {
      fontSize: 11,
      color: colors.textMuted,
      marginLeft: "auto",
    },
    hidden: {
      display: "none",
    },
    searchContainer: {
      flexDirection: "row",
      flexWrap: "nowrap",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#444" : "#D1D5DB",
      borderRadius: 8,
      paddingHorizontal: 8,
      paddingVertical: 4,
      backgroundColor: colors.surface,
      minHeight: 36,
      width: 320,
    },
    searchChip: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: isDark ? colors.primary + "33" : "#DBEAFE",
      borderRadius: 20,
      paddingLeft: 10,
      paddingRight: 6,
      paddingVertical: 4,
      marginRight: 4,
      marginVertical: 2,
    },
    searchChipText: {
      fontSize: 12,
      color: isDark ? "#93C5FD" : "#1D4ED8",
      fontWeight: "600",
    },
    searchChipRemove: {
      marginLeft: 6,
      width: 16,
      height: 16,
      borderRadius: 8,
      backgroundColor: isDark ? colors.primary + "66" : "#93C5FD",
      justifyContent: "center",
      alignItems: "center",
    },
    searchChipX: {
      fontSize: 9,
      color: isDark ? "#fff" : "#1E40AF",
      fontWeight: "700",
      lineHeight: 16,
    },
    searchInputInner: {
      flex: 1,
      minWidth: 120,
      fontSize: 13,
      height: 24,
      padding: 0,
      color: colors.text,
    },
    viewToggle: {
      flexDirection: "row",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 4,
      overflow: "hidden",
    },
    viewButton: {
      paddingHorizontal: 12,
      paddingVertical: 5,
      backgroundColor: colors.surface,
    },
    viewButtonActive: {
      backgroundColor: "#1976d2",
    },
    viewButtonText: {
      fontSize: 11,
      color: colors.textSecondary,
    },
    viewButtonTextActive: {
      color: "#fff",
      fontWeight: "700",
    },
    downloadButton: {
      width: 32,
      height: 32,
      borderRadius: 6,
      backgroundColor: isDark ? "#333" : "#F3F4F6",
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#444" : "#E2E8F0",
    },
    listHeaderRow: {
      flexDirection: "row",
      alignItems: "stretch",
      paddingVertical: 8,
      borderTopWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    listHeaderText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
      paddingHorizontal: 6,
    },
    listGroupRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderColor: colors.border,
      backgroundColor: isDark ? "#1c1c1c" : "#fafafa",
    },
    listGroupLabel: {
      flex: 1,
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      paddingHorizontal: 6,
    },
    listGroupCell: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      textAlign: "right",
      paddingHorizontal: 6,
    },
    listRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 6,
      borderBottomWidth: 1,
      borderColor: colors.borderSubtle,
    },
    listRowAlt: {
      backgroundColor: isDark ? "#171717" : "#fcfcfc",
    },
    listCell: {
      fontSize: 11,
      color: colors.text,
      paddingHorizontal: 6,
    },
    listCellRight: {
      textAlign: "right",
    },
    statusChip: {
      alignSelf: "flex-start",
      marginHorizontal: 6,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 10,
      backgroundColor: isDark ? "#123" : "#e0f2f1",
    },
    statusChipText: {
      fontSize: 10,
      color: isDark ? "#8ecfcf" : "#00695c",
    },
    listTotalRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 8,
      borderTopWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    listTotalCell: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      textAlign: "right",
      paddingHorizontal: 6,
    },
    popover: {
      position: "absolute",
      width: POPOVER_W,
      zIndex: 20,
      borderRadius: 6,
      backgroundColor: isDark ? "#2b2b2b" : "#ffffff",
      shadowColor: "#000",
      shadowOpacity: 0.25,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 4 },
      elevation: 8,
    },
    popoverCaret: {
      position: "absolute",
      top: -CARET_H,
      width: 0,
      height: 0,
      borderLeftWidth: CARET_H,
      borderRightWidth: CARET_H,
      borderBottomWidth: CARET_H,
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderBottomColor: isDark ? "#3a3a3a" : "#e9e9ea",
    },
    // Used when the card is flipped above its bar; points down at the bar.
    popoverCaretBottom: {
      position: "absolute",
      bottom: -CARET_H,
      width: 0,
      height: 0,
      borderLeftWidth: CARET_H,
      borderRightWidth: CARET_H,
      borderTopWidth: CARET_H,
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderTopColor: isDark ? "#2b2b2b" : "#ffffff",
    },
    popoverHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      padding: 12,
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      backgroundColor: isDark ? "#3a3a3a" : "#e9e9ea",
    },
    popoverTitle: {
      flex: 1,
      fontSize: 13,
      fontWeight: "700",
      lineHeight: 18,
      color: colors.text,
      paddingRight: 8,
    },
    popoverClose: {
      paddingHorizontal: 4,
    },
    popoverCloseText: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
    },
    popoverBody: {
      padding: 12,
      gap: 4,
    },
    popoverRow: {
      fontSize: 12,
      color: colors.text,
    },
    popoverLabel: {
      fontWeight: "700",
      color: colors.text,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    loadingText: {
      marginTop: 10,
      fontSize: 12,
      color: colors.textMuted,
    },
    headerRow: {
      flexDirection: "row",
      borderTopWidth: 1,
      borderLeftWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    headerClip: {
      flex: 1,
      overflow: "hidden",
    },
    cornerCell: {
      height: MONTH_H + DAY_H,
      justifyContent: "center",
      paddingLeft: 8,
      borderRightWidth: 1,
      borderColor: colors.border,
    },
    cornerText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
    },
    monthRow: {
      flexDirection: "row",
      height: MONTH_H,
    },
    monthCell: {
      height: MONTH_H,
      justifyContent: "center",
      paddingLeft: 6,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
    },
    monthText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
    },
    dayRow: {
      flexDirection: "row",
      height: DAY_H,
    },
    dayCell: {
      width: DAY_W,
      height: DAY_H,
      justifyContent: "center",
      alignItems: "center",
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
    },
    dayText: {
      fontSize: 10,
      color: colors.textMuted,
    },
    weekendCell: {
      backgroundColor: isDark ? "#1b1b1b" : "#eeeeee",
    },
    todayCell: {
      backgroundColor: isDark ? "#2a2410" : "#fff8e1",
    },
    labelCell: {
      justifyContent: "center",
      paddingLeft: 8,
      borderLeftWidth: 1,
      borderBottomWidth: 1,
      borderRightWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    labelText: {
      fontSize: 12,
      color: colors.text,
    },
    gridRow: {
      position: "relative",
      borderBottomWidth: 1,
      borderColor: colors.border,
    },
    gridCell: {
      position: "absolute",
      top: 0,
      bottom: 0,
      width: DAY_W,
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
    },
    bar: {
      position: "absolute",
      height: BAR_H,
      borderRadius: 3,
      borderWidth: 1,
      justifyContent: "center",
      paddingHorizontal: 5,
      overflow: "hidden",
    },
    barText: {
      fontSize: 10,
      color: "#2b2b2b",
    },
    footerRow: {
      flexDirection: "row",
      borderLeftWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    footerLabel: {
      height: TOTAL_H,
      justifyContent: "center",
      alignItems: "flex-end",
      paddingRight: 10,
      borderRightWidth: 1,
      borderColor: colors.border,
    },
    footerLabelText: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
    },
    totalCell: {
      width: DAY_W,
      height: TOTAL_H,
      justifyContent: "center",
      alignItems: "center",
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
    },
    totalText: {
      fontSize: 9,
      color: colors.textSecondary,
    },
  });
